// src/components/TermsAndConditions.js
import React from 'react';
import './TermsAndConditions.css';

const TermsAndConditions = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="terms-hero">
        <div className="container">
          <h1 className="terms-title">Terms and Conditions</h1>
          <p className="terms-last-updated">Last Updated: January 2026</p>
        </div>
      </section>

      <main className="terms-main container">
        {/* Navigation Sidebar */}
        <aside className="terms-sidebar">
          <h4>Navigation</h4>
          <ul>
            <li><a href="#introduction">Introduction</a></li>
            <li><a href="#about">About Oxymed Billing Solutions</a></li>
            <li><a href="#eligibility">Eligibility and Use of Website</a></li>
            <li><a href="#scope">Scope of Services</a></li>
            <li><a href="#advice">No Medical or Legal Advice</a></li>
            <li><a href="#hipaa">HIPAA Compliance and Data Protection</a></li>
            <li><a href="#intellectual">Intellectual Property Rights</a></li>
            <li><a href="#responsibilities">Client Responsibilities</a></li>
            <li><a href="#payments">Payments and Fees</a></li>
            <li><a href="#liability">Limitation of Liability</a></li>
            <li><a href="#disclaimer">Disclaimer of Warranties</a></li>
            <li><a href="#thirdparty">Third-Party Links</a></li>
            <li><a href="#termination">Termination of Services</a></li>
            <li><a href="#governing">Governing Law</a></li>
            <li><a href="#changes">Changes to Terms and Conditions</a></li>
            <li><a href="#contact">Contact Information</a></li>
          </ul>
        </aside>

        {/* Main Content */}
        <section className="terms-content">
          <section id="introduction">
            <h2>1. Introduction</h2>
            <p>
              Welcome to <strong>Oxymed Billing Solutions</strong>. These Terms and Conditions govern your access to and use of our website, services, 
              and solutions related to medical billing, coding, revenue cycle management, and virtual healthcare support.
            </p>
            <p>
              By accessing our website or using our services, you agree to be legally bound by these Terms and Conditions. 
              If you do not agree with any part of these terms, you should not use our website or services.
            </p>
          </section>

          <section id="about">
            <h2>2. About Oxymed Billing Solutions</h2>
            <p>
              Oxymed Billing Solutions is a healthcare support services company providing medical billing, medical coding, revenue cycle management (RCM), 
              and virtual assistant services to healthcare providers across the United States.
            </p>
            <p>
              Our services are designed to support operational efficiency, regulatory compliance, and revenue optimization for medical practices, clinics, 
              and healthcare organizations.
            </p>
          </section>

          <section id="eligibility">
            <h2>3. Eligibility and Use of Website</h2>
            <ul>
              <li>You are at least 18 years of age.</li>
              <li>You are authorized to act on behalf of a healthcare practice or business if engaging our services.</li>
              <li>You will use the website for lawful purposes only.</li>
            </ul>
            <p>
              You agree not to misuse the website, attempt unauthorized access, or interfere with the website’s functionality, security, or data.
            </p>
          </section>

          <section id="scope">
            <h2>4. Scope of Services</h2>
            <ul>
              <li>Medical billing and claims management</li>
              <li>Medical coding services</li>
              <li>Revenue cycle management</li>
              <li>Insurance verification and eligibility checks</li>
              <li>Prior authorization support</li>
              <li>Virtual healthcare administrative support</li>
            </ul>
            <p>
              Specific service details, pricing, and timelines are defined in separate service agreements or contracts executed with clients.
            </p>
          </section>

          <section id="advice">
            <h2>5. No Medical or Legal Advice</h2>
            <p>
              Oxymed Billing Solutions does not provide medical diagnosis, treatment, or legal advice. Our services are administrative and operational in nature and are intended to support healthcare providers.
            </p>
            <ul>
              <li>Clinical decision-making</li>
              <li>Patient care</li>
              <li>Compliance with applicable medical, legal, and regulatory requirements</li>
            </ul>
          </section>

          <section id="hipaa">
            <h2>6. HIPAA Compliance and Data Protection</h2>
            <ul>
              <li>All client data is handled in accordance with HIPAA and applicable healthcare regulations</li>
              <li>Access to sensitive information is restricted to authorized personnel only</li>
              <li>Clients are responsible for ensuring proper authorization before sharing patient data with us</li>
            </ul>
            <p>Please review our Privacy Policy for detailed information on data handling and security practices.</p>
          </section>

          <section id="intellectual">
            <h2>7. Intellectual Property Rights</h2>
            <p>
              All content on this website, including text, graphics, logos, icons, and software, is the property of Oxymed Billing Solutions or its licensors and is protected by applicable intellectual property laws.
            </p>
            <p>
              You may not copy, reproduce, distribute, or modify any content without prior written consent from Oxymed Billing Solutions.
            </p>
          </section>

          <section id="responsibilities">
            <h2>8. Client Responsibilities</h2>
            <ul>
              <li>Provide accurate and complete information required for service delivery</li>
              <li>Maintain proper documentation and records</li>
              <li>Comply with payer, federal, and state regulations</li>
              <li>Review reports and communications provided by Oxymed Billing Solutions in a timely manner</li>
            </ul>
          </section>

          <section id="payments">
            <h2>9. Payments and Fees</h2>
            <p>
              Service fees, billing cycles, and payment terms are outlined in individual service agreements.
            </p>
            <p>
              Late or non-payment may result in service suspension or termination. Oxymed Billing Solutions reserves the right to revise pricing with prior notice.
            </p>
          </section>

          <section id="liability">
            <h2>10. Limitation of Liability</h2>
            <ul>
              <li>Oxymed Billing Solutions shall not be liable for indirect, incidental, or consequential damages</li>
              <li>We do not guarantee specific reimbursement amounts or payer decisions</li>
              <li>Liability is limited to the fees paid for services during the applicable service period</li>
            </ul>
          </section>

          <section id="disclaimer">
            <h2>11. Disclaimer of Warranties</h2>
            <p>
              All services and website content are provided on an “as is” and “as available” basis.
            </p>
            <p>
              We make no warranties, express or implied, regarding uninterrupted service, accuracy of information, or suitability for a specific purpose.
            </p>
          </section>

          <section id="thirdparty">
            <h2>12. Third-Party Links</h2>
            <p>
              Our website may contain links to third-party websites for informational purposes. Oxymed Billing Solutions does not control or endorse these websites and is not responsible for their content, policies, or practices.
            </p>
          </section>

          <section id="termination">
            <h2>13. Termination of Services</h2>
            <ul>
              <li>These Terms are violated</li>
              <li>There is misuse of services</li>
              <li>Legal or compliance risks arise</li>
            </ul>
            <p>Termination terms are further governed by individual service agreements.</p>
          </section>

          <section id="governing">
            <h2>14. Governing Law</h2>
            <p>
              These Terms and Conditions shall be governed by and interpreted in accordance with the laws of the United States, without regard to conflict of law principles.
            </p>
          </section>

          <section id="changes">
            <h2>15. Changes to Terms and Conditions</h2>
            <p>
              Oxymed Billing Solutions reserves the right to update or modify these Terms and Conditions at any time. Changes will be posted on this page with an updated revision date.
            </p>
            <p>
              Continued use of our website or services constitutes acceptance of the updated terms.
            </p>
          </section>

          <section id="contact">
            <h2>16. Contact Information</h2>
            <p>Email: info@oxymedbs.com</p>
            <p>Website: oxymedbs.com</p>
            <p>Oxymed Billing Solutions</p>
          </section>
        </section>
      </main>

      
    </>
  );
};

export default TermsAndConditions;